import React from 'react';
import { format } from 'date-fns';
import {
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Collapse,
} from '@material-ui/core';
import UpIcon from '@mui/icons-material/ArrowDropUp';
import DownIcon from '@mui/icons-material/ArrowDropDown';
import { useSelector } from 'react-redux';

import { selectUser } from 'entities/user/selectors';
import { ProviderType } from 'generated/graphql';
import { TableBox } from 'retired/Box';
import { CurrencyCode } from 'utils/currencyFormat';
import { Privileges } from 'utils/privileges';

import { TransactionRow } from './TransactionRow';
import StatsSection from './StatsSection';
import Counter from './Counter';
import { TransactionsAggregate } from './summarizeTransactions';

const formatMonth = (monthKey: string): string => {
  const monthDate = new Date(monthKey);
  return format(
    monthDate,
    monthDate.getUTCFullYear() < new Date().getUTCFullYear() ? 'MMMM yyyy' : 'MMMM'
  );
};

export const ProcessedTransactionsTable = ({
  processedTransactionsAggregate,
  accountCurrency,
  accountProvider,
}: {
  processedTransactionsAggregate: TransactionsAggregate;
  accountCurrency: CurrencyCode;
  accountProvider: ProviderType;
}): JSX.Element => {
  const [open, setOpen] = React.useState(true);
  const user = useSelector(selectUser);

  const userCanManageBudgetLedger = user?.hasPrivilege(Privileges.BUDGET_LEDGER_MANAGE) || false;

  // @TODO extract this logic
  const firstTransactionMonth = (processedTransactionsAggregate.transactions || [])[
    processedTransactionsAggregate.transactions.length - 1
  ];
  const openingBalanceMonth = firstTransactionMonth
    ? (firstTransactionMonth?.balance || 0) -
      (firstTransactionMonth?.__typename === 'GivingAccountWithdrawal'
        ? -1 * (firstTransactionMonth.totalAccountCurrency || 0)
        : firstTransactionMonth?.amount || 0)
    : 0;
  const closingBalanceMonth = processedTransactionsAggregate.transactions[0]?.balance || 0;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: 24,
          marginBottom: 12,
        }}
      >
        <Typography
          component="h3"
          onClick={(): void => setOpen(!open)}
          style={{
            fontSize: 18,
            fontWeight: 'bold',
            flexGrow: 1,
            marginLeft: 16,
          }}
        >
          <IconButton aria-label="expand row" size="small">
            {open ? <UpIcon /> : <DownIcon />}
          </IconButton>
          {formatMonth(processedTransactionsAggregate.monthKey)}
        </Typography>
        <StatsSection>
          <Counter
            isFirst
            label="Opening balance"
            amount={openingBalanceMonth}
            accountCurrency={accountCurrency}
          />
          <Counter
            label="Closing balance"
            amount={closingBalanceMonth}
            accountCurrency={accountCurrency}
          />
          <Counter
            label="Funds received"
            amount={processedTransactionsAggregate.receivedAmount}
            accountCurrency={accountCurrency}
          />
          <Counter
            label="Funds paid"
            amount={processedTransactionsAggregate.paidAmount}
            accountCurrency={accountCurrency}
          />
        </StatsSection>
      </div>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <TableBox>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  size="medium"
                  style={{
                    width: 43,
                    maxWidth: '5%',
                  }}
                />
                <TableCell
                  size="medium"
                  align="left"
                  style={{
                    paddingLeft: 0,
                    width: '10%',
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '10%',
                  }}
                >
                  Transaction
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: userCanManageBudgetLedger ? '25%' : '45%',
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '10%',
                  }}
                >
                  Processor
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '12.5%',
                  }}
                >
                  Total Amount
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '12.5%',
                  }}
                >
                  Balance
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '15%',
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {processedTransactionsAggregate.transactions.filter(Boolean).map((transaction) => (
                  <TransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    accountCurrency={accountCurrency}
                    accountProvider={accountProvider}
                  />
                ))}
              </>
            </TableBody>
          </Table>
        </TableBox>
      </Collapse>
    </div>
  );
};
export default ProcessedTransactionsTable;
